/**
 * DisconnectionTimer Component
 *
 * Displays countdown until auto-unassignment of disconnected socket.
 * Shows progress bar and changes color based on time remaining.
 *
 * Features:
 * - Real-time countdown
 * - Progress bar visualization
 * - Color-coded warning levels
 * - Accessible
 *
 * Issue: #1 - Auto-Unassignment
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Paper,
  makeStyles,
} from '@material-ui/core';
import { Timer, Warning } from '@material-ui/icons';
import Alert from '@material-ui/lab/Alert';

const useStyles = makeStyles((theme) => ({
  timerIcon: {
    fontSize: 20,
    marginRight: theme.spacing(1),
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    marginBottom: theme.spacing(1),
  },
  compactBox: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  fullPaper: {
    padding: theme.spacing(2),
    borderWidth: 2,
    borderStyle: 'solid',
  },
}));

const DisconnectionTimer = ({
  disconnectedAt,
  delayMinutes,
  onExpire,
  showProgress = true,
  compact = false,
}) => {
  const classes = useStyles();
  const [now, setNow] = useState(new Date());
  const [hasExpired, setHasExpired] = useState(false);

  // Update current time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Calculate time remaining
  const timeData = useMemo(() => {
    const disconnectTime = new Date(disconnectedAt);
    const expiryTime = new Date(
      disconnectTime.getTime() + delayMinutes * 60 * 1000
    );
    const secondsRemaining = Math.floor((expiryTime - now) / 1000);
    const totalSeconds = delayMinutes * 60;
    const percentage = Math.max(
      0,
      Math.min(100, (secondsRemaining / totalSeconds) * 100)
    );

    const minutes = Math.floor(Math.abs(secondsRemaining) / 60);
    const seconds = Math.abs(secondsRemaining) % 60;

    // Calculate relative time string manually
    const minutesAgo = Math.floor((now - disconnectTime) / 60000);
    let relativeTime;
    if (minutesAgo < 1) {
      relativeTime = 'just now';
    } else if (minutesAgo === 1) {
      relativeTime = '1 minute ago';
    } else if (minutesAgo < 60) {
      relativeTime = `${minutesAgo} minutes ago`;
    } else {
      const hoursAgo = Math.floor(minutesAgo / 60);
      relativeTime = hoursAgo === 1 ? '1 hour ago' : `${hoursAgo} hours ago`;
    }

    return {
      expiryTime,
      secondsRemaining,
      totalSeconds,
      percentage,
      minutes,
      seconds,
      expired: secondsRemaining <= 0,
      relativeTime,
    };
  }, [disconnectedAt, delayMinutes, now]);

  // Handle expiration
  useEffect(() => {
    if (timeData.expired && !hasExpired) {
      setHasExpired(true);
      if (onExpire) {
        onExpire();
      }
    }
  }, [timeData.expired, hasExpired, onExpire]);

  // Determine severity based on time remaining
  const getSeverity = () => {
    const minutesRemaining = timeData.secondsRemaining / 60;
    if (minutesRemaining < 1) return 'error';
    if (minutesRemaining < 2) return 'warning';
    return 'info';
  };

  const severity = getSeverity();

  // Color mapping
  const getColor = () => {
    switch (severity) {
      case 'error':
        return '#f44336'; // Red
      case 'warning':
        return '#ff9800'; // Orange
      default:
        return '#2196f3'; // Blue
    }
  };

  const color = getColor();

  // If expired
  if (timeData.expired) {
    return (
      <Alert severity="error" icon={<Warning />}>
        <Typography variant="body2" style={{ fontWeight: 'bold' }}>
          Auto-unassignment time reached
        </Typography>
        <Typography variant="caption">
          Service should be automatically ending...
        </Typography>
      </Alert>
    );
  }

  // Compact mode
  if (compact) {
    return (
      <Box className={classes.compactBox} role="timer" aria-live="polite">
        <Timer style={{ fontSize: 18, color }} />
        <Typography variant="body2" style={{ color, fontWeight: 'bold' }}>
          {timeData.minutes}m {timeData.seconds}s
        </Typography>
      </Box>
    );
  }

  // Full mode
  return (
    <Paper
      elevation={0}
      className={classes.fullPaper}
      style={{
        borderColor: color,
        backgroundColor: `${color}18`,
      }}
    >
      <Box display="flex" alignItems="center" mb={1}>
        <Timer className={classes.timerIcon} style={{ color }} />
        <Typography
          variant="body2"
          style={{ fontWeight: 'bold' }}
          role="timer"
          aria-live="assertive"
        >
          Auto-unassignment in:{' '}
          <span style={{ color }}>
            {timeData.minutes}m {timeData.seconds}s
          </span>
        </Typography>
      </Box>

      {showProgress && (
        <LinearProgress
          variant="determinate"
          value={timeData.percentage}
          className={classes.progressBar}
          style={{
            backgroundColor: '#e0e0e0',
          }}
          classes={{
            bar: {
              backgroundColor: color,
            },
          }}
        />
      )}

      <Typography variant="caption" color="textSecondary">
        Disconnected {timeData.relativeTime}
      </Typography>
    </Paper>
  );
};

export default DisconnectionTimer;
